import {z} from 'zod';

import {Database} from '@/types/database.types';
import {FlattenT} from '@/types/utility.types';

import {
  publicBrandInsertSchemaSchema,
  publicDistributorInsertSchemaSchema,
  publicRetailerInsertSchemaSchema,
  publicSpeculatorUpdateSchemaSchema,
} from './generated-schemas';

// Schemas
export * from './generated-schemas';

/*
 * Setup
 */
export type SchemaShapeT = z.ZodRawShape;
export type RegistrationEntityTypeT = 'speculator' | 'brand' | 'retailer' | 'distributor';

// Cache for combined schemas
const schemaCache = new Map<string, z.ZodObject<SchemaShapeT>>();

// System fields used in many tables
export const systemFieldsSchema = z.object({
  created_by: z.string().uuid(),
  date_created: z.string(),
  date_modified: z.string(),
  modified_by: z.string().uuid().nullable(),
  removed_by: z.string().uuid().nullable(),
  date_removed: z.string().nullable(),
});

export type SystemFieldsT = keyof z.infer<typeof systemFieldsSchema>;

const systemFieldKeys: Record<SystemFieldsT, true> = {
  created_by: true,
  date_created: true,
  date_modified: true,
  modified_by: true,
  removed_by: true,
  date_removed: true,
};

/**
 * Get a cached combined schema or create and cache it
 * @param baseSchema - The base schema to combine with
 * @param additionalSchema - The additional schema to merge
 * @param cacheKey - Unique key for caching
 */
export function getCombinedSchema(
  baseSchema: z.ZodObject<SchemaShapeT>,
  additionalSchema: z.ZodObject<SchemaShapeT>,
  cacheKey: string
): z.ZodObject<SchemaShapeT> {
  if (!schemaCache.has(cacheKey)) {
    const merged = baseSchema.merge(additionalSchema);
    schemaCache.set(cacheKey, merged);
    return merged;
  }
  return schemaCache.get(cacheKey)!;
}

export type RegFormKeysBasedOnAccountT<T extends RegistrationEntityTypeT> = FlattenT<
  Omit<
    Database['public']['Tables']['Speculator']['Update'] & // Always include speculator fields
      (T extends 'brand'
        ? Database['public']['Tables']['Brand']['Insert']
        : T extends 'retailer'
          ? Database['public']['Tables']['Retailer']['Insert']
          : T extends 'distributor'
            ? Database['public']['Tables']['Distributor']['Insert']
            : Record<string, never>) & {
        terms_accepted: boolean;
      },
    SystemFieldsT
  >
>;

/**
 * Get a registration schema without any system fields
 * @param type - The type of registration entity
 */
export const getRegistrationSchema = (type: RegistrationEntityTypeT = 'speculator'): z.ZodObject<z.ZodRawShape> => {
  const termsSchema = z.object({
    terms_accepted: z.boolean().refine((data: boolean) => data, {
      message: 'You must agree to all Terms & Conditions',
    }),
  });

  const schemas = {
    speculator: publicSpeculatorUpdateSchemaSchema,
    brand: publicBrandInsertSchemaSchema,
    retailer: publicRetailerInsertSchemaSchema,
    distributor: publicDistributorInsertSchemaSchema,
  } as const;

  const schema = z
    .object({
      ...schemas.speculator.shape,
      ...(type !== 'speculator' ? schemas[type].shape : {}),
      ...termsSchema.shape,
    })
    .omit(systemFieldKeys);

  return schema;
};
